import React, { useMemo, useState } from "react";
import {
  Box,
  Checkbox,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";
import { useThemeColors } from "../../../hooks/useThemeColors";
import { humanize } from "../roles/permissionUtils";
import { ApiApplicationGrant, ExternalResource } from "./types";

interface ApiTableChecklistProps {
  resources: ExternalResource[];
  grants: Record<string, ApiApplicationGrant>;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  onToggleView: (resource: ExternalResource) => void;
  onShowDocs: (resource: ExternalResource) => void;
  onTry: (resource: ExternalResource) => void;
  disabled?: boolean;
}

/**
 * Flat checklist of the tables the external API serves: one row per
 * registry resource with a View checkbox, docs and "try it" shortcuts.
 * Granted rows sort first so the edit page reads as a preview of access.
 */
const ApiTableChecklist: React.FC<ApiTableChecklistProps> = ({
  resources,
  grants,
  loading,
  error,
  onRetry,
  onToggleView,
  onShowDocs,
  onTry,
  disabled = false,
}) => {
  const colors = useThemeColors();
  const headingColor = colors.primary[100];
  const accentColor = colors.primary[400];
  const [search, setSearch] = useState("");

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    const isGranted = (r: ExternalResource) => !!grants[r.key]?.can_view;
    return resources
      .filter(
        (r) =>
          !term ||
          r.slug.includes(term) ||
          r.model.includes(term) ||
          r.app_label.includes(term) ||
          r.description.toLowerCase().includes(term),
      )
      .slice()
      .sort((a, b) => Number(isGranted(b)) - Number(isGranted(a)));
  }, [resources, grants, search]);

  if (loading) {
    return (
      <Stack spacing={1}>
        {[0, 1, 2, 3, 4].map((i) => (
          <Skeleton key={i} variant="rounded" height={56} sx={{ borderRadius: 2 }} />
        ))}
      </Stack>
    );
  }

  if (error) {
    return (
      <Paper
        sx={{ boxShadow: 0, border: "1px dashed", borderColor: "divider", borderRadius: 3, p: 3, textAlign: "center" }}
      >
        <Typography color="text.secondary" sx={{ mb: 1 }}>
          {error}
        </Typography>
        <Typography
          component="button"
          type="button"
          onClick={onRetry}
          sx={{ background: "none", border: 0, cursor: "pointer", color: accentColor, fontWeight: 600 }}
        >
          Retry
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      <TextField
        size="small"
        placeholder="Search tables…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        fullWidth
        autoComplete="off"
        inputProps={{ name: "api-table-search", "aria-label": "Search tables" }}
        sx={{ mb: 1.5 }}
      />
      <Stack spacing={1}>
        {rows.length === 0 && (
          <Typography color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
            No tables match &quot;{search}&quot;.
          </Typography>
        )}
        {rows.map((resource) => {
          const granted = !!grants[resource.key]?.can_view;
          return (
            <Paper
              key={resource.key}
              sx={{
                boxShadow: 0,
                border: "1px solid",
                borderColor: "divider",
                borderLeft: "4px solid",
                borderLeftColor: granted ? accentColor : "divider",
                borderRadius: 2,
                px: 1.5,
                py: 1,
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
                flexShrink: 0,
                opacity: disabled ? 0.6 : 1,
              }}
            >
              <Checkbox
                size="small"
                color="primary"
                checked={granted}
                disabled={disabled}
                onChange={() => onToggleView(resource)}
                inputProps={{ "aria-label": `View ${humanize(resource.model)}` }}
              />
              <Box flexGrow={1} minWidth={160}>
                <Typography fontWeight={600} sx={{ color: headingColor }}>
                  {humanize(resource.model)}
                  <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    {resource.app_label} · /{resource.slug}/
                  </Typography>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {resource.description}
                </Typography>
              </Box>
              <Stack direction="row" spacing={0.5}>
                <Tooltip title="API documentation">
                  <IconButton size="small" onClick={() => onShowDocs(resource)}>
                    <MenuBookOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Try it with a token">
                  <IconButton size="small" onClick={() => onTry(resource)}>
                    <PlayArrowOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Paper>
          );
        })}
      </Stack>
    </Box>
  );
};

export default ApiTableChecklist;
